import React from 'react';

import { Box, ClassNameMap } from '@mui/material';
import { MatchingProfileData } from '@uplift/api';
import { useL10n } from '@uplift/hooks';

import { PreferredInsuranceMatch } from '../../preferred-insurance-match';
import { MatchingAvatar } from './avatar';
import { ProviderHeader } from './provider-header';
import { useNextAvailableDateFormatted } from './provider-item.hooks';
import { useProviderItemGrid, useProviderItemStyles } from './styles';

type ProviderItemProps = {
  onView: () => void;
  preferredInsuranceCompanyId?: string;
  stateId: string;
  matchingProfile: MatchingProfileData;
  classes?: Partial<ClassNameMap<'root'>>;
  isBookingSponsored?: boolean;
};

export const ProviderItem: React.FC<ProviderItemProps> = ({ matchingProfile, onView, preferredInsuranceCompanyId, stateId, isBookingSponsored, ...props }) => {
  const l10n = useL10n();
  const { classes } = useProviderItemStyles();
  const { classes: gridClasses } = useProviderItemGrid({ classes: props.classes }, { props: { classes: props.classes } });

  const nextAppointmentDate = useNextAvailableDateFormatted(matchingProfile?.nextAvailableDate);
  const bio = matchingProfile.profile.shortBio ?? 'No bio';
  const insuranceCompany = matchingProfile.supportedInsuranceCompanies?.find((insuranceCompany) => insuranceCompany.id === preferredInsuranceCompanyId);
  const specialties = matchingProfile.specialties.map((specialty) => specialty.name).join(', ');

  return (
    <Box className={gridClasses.root} onClick={onView}>
      <Box className={gridClasses.grid}>
        <Box className={gridClasses.avatar}>
          <MatchingAvatar avatarUrl={matchingProfile.avatarAsset.url} nextAvailableDate={nextAppointmentDate} />
        </Box>
        <Box className={gridClasses.title}>
          <ProviderHeader
            providerName={matchingProfile.nameDisplay.defaultDisplay}
            pronouns={matchingProfile.profile.pronouns}
            licenses={matchingProfile.licenses}
            stateId={stateId}
            medicalRole={matchingProfile.user.medicalRole}
          />
        </Box>
        {!isBookingSponsored && (
          <Box className={gridClasses.accepts}>
            <PreferredInsuranceMatch insuranceCompany={insuranceCompany} />
          </Box>
        )}
        <Box className={gridClasses.bio}>
          <Box className={classes.bio}>
            {bio}
          </Box>
        </Box>
        <Box className={gridClasses.specialties}>
          <span className={classes.specialties}>{l10n.matching.providers.specialties}</span>: {specialties}
        </Box>
        <Box className={gridClasses.nextAvailable}>
          {l10n.matching.providers.nextAvailable} {nextAppointmentDate}
        </Box>
      </Box>
    </Box>
  );
};
